import { useState } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { findCountry } from '@repletoryreach/sdk-utils/phone';
import { Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { CountryPicker, usePhoneDefaultCountry, type PhoneRefusalDetail } from '@repletoryreach/sdk-ui/inputs';
import { openModal, closeModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { phoneLabels } from '../../../lib/phone';
import { runImportWithProgress } from '../import-progress';
import { useContactFields } from './tabs/FieldsTab';

/** How many refused rows the modal lists; the count is always complete. */
const LISTED = 25;

/**
 * What the file will do, before it does it.
 *
 * Every number goes through the same rule the service runs, with the
 * organisation's default country: "Assumed South Africa (+27) for 12
 * numbers; 3 numbers could not be read", the rows that failed with the
 * reason, and the country changeable before committing. Rows that fail are
 * skipped and reported, never dropped without a word.
 */
export function openImportPreview(file: File, qc: QueryClient, title: string): void {
  let id = '';
  id = openModal({
    title,
    children: <ImportPreview file={file} qc={qc} onDone={() => closeModal(id)} />
  });
}

function ImportPreview({ file, qc, onDone }: { file: File; qc: QueryClient; onDone: () => void }) {
  const t = useT();
  const fallback = usePhoneDefaultCountry();
  const [country, setCountry] = useState(fallback);
  // Unknown headers become text fields on import when this is ticked.
  const [createFields, setCreateFields] = useState(false);
  const labels = phoneLabels(t);
  const { defs } = useContactFields();

  const preview = useQuery({
    queryKey: ['contacts', 'import-preview', file.name, file.size, file.lastModified, country],
    queryFn: () => contacts.previewImport(file, { default_country: country }),
    select: (r) => r.data,
    retry: false,
    staleTime: 5 * 60_000
  });
  const r = preview.data;
  // Rows with no number and an address (4 October 2026): imported as
  // email-only contacts. Not in the SDK's generated type yet.
  const emailOnly = (r as { email_only_rows?: number } | undefined)?.email_only_rows ?? 0;
  const chosen = findCountry(country);

  // The same sentence the field would show for that row.
  const why = (f: contacts.ImportFailedRow) => {
    const say = labels.reasons?.[f.reason as keyof NonNullable<typeof labels.reasons>];
    const detail: PhoneRefusalDetail = { country: findCountry(f.assumed_country), expected: f.expected_digits ?? undefined };
    return say ? say(detail) : f.message;
  };

  function commit() {
    onDone();
    void runImportWithProgress(file, t, qc, country, createFields && (r?.unknown_columns.length ?? 0) > 0);
  }

  /** A mapping target as a person reads it: a base column by name, a field by its label. */
  function describeTarget(target: string): string {
    if (target === 'ignored') return t('contacts.import.preview.target.ignored');
    if (target.startsWith('field:')) {
      const key = target.slice('field:'.length);
      return t('contacts.import.preview.target.field', { label: defs.find((d) => d.key === key)?.label ?? key });
    }
    return target;
  }

  return (
    <Stack gap="md">
      <CountryPicker
        label={t('contacts.import.preview.country')}
        description={t('contacts.import.preview.countryHint')}
        value={country}
        onChange={setCountry}
        labels={labels}
      />

      {preview.isPending && (
        <Stack gap="xs">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('contacts.import.preview.reading', { file: file.name })}</Text>
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="50%" />
        </Stack>
      )}

      {preview.isError && (
        <Alert color="error">
          {preview.error instanceof Error ? preview.error.message : t('contacts.import.preview.failedToRead')}
        </Alert>
      )}

      {r && (
        <Stack gap="xs">
          {r.assumed_count > 0 && (
            <Text role="bodySmall">
              {t('contacts.import.preview.assumed', { country: chosen?.name ?? country, dial: r.assumed_dial_code, count: r.assumed_count })}
            </Text>
          )}
          {r.international_count > 0 && (
            <Text role="bodySmall">{t('contacts.import.preview.international', { count: r.international_count })}</Text>
          )}
          {emailOnly > 0 && (
            <Text role="bodySmall">{t('contacts.import.emailOnlyRows', { count: emailOnly })}</Text>
          )}
          {r.sample[0] && (
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('contacts.import.preview.sample', { input: r.sample[0].input, e164: r.sample[0].e164 })}
            </Text>
          )}
          {r.failed_count === 0 ? (
            <Text role="bodySmall" c="var(--rr-color-success, inherit)">{t('contacts.import.preview.allGood')}</Text>
          ) : (
            <Alert color="warning">
              <Stack gap="xs">
                <Text role="bodySmall">{t('contacts.import.preview.failed', { count: r.failed_count })}</Text>
                <table className="rr-contacts__csv-cols">
                  <thead>
                    <tr>
                      <th>{t('contacts.import.preview.row')}</th>
                      <th>{t('contacts.import.preview.input')}</th>
                      <th>{t('contacts.import.preview.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.failed.slice(0, LISTED).map((f) => (
                      <tr key={f.row}>
                        <td>{f.row}</td>
                        <td><code>{f.input}</code></td>
                        <td>{why(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {r.failed_count > LISTED && (
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('contacts.import.preview.moreFailed', { count: r.failed_count - LISTED })}
                  </Text>
                )}
              </Stack>
            </Alert>
          )}
          {r.ok_rows === 0 && <Alert color="error">{t('contacts.import.preview.nothing')}</Alert>}

          {/* Where each column lands. The importer reads the header now, so
              a file with the columns in any order, or with a custom field's
              key or label as a header, is placed rather than guessed at. */}
          {Object.keys(r.mapped_columns ?? {}).length > 0 && (
            <Stack gap="xs" style={{ paddingTop: 6 }}>
              <Text role="bodySmall" fw={650}>{t('contacts.import.preview.mapping')}</Text>
              <table className="rr-contacts__csv-cols">
                <thead>
                  <tr>
                    <th>{t('contacts.import.preview.column')}</th>
                    <th>{t('contacts.import.preview.target')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(r.mapped_columns).map(([header, target]) => (
                    <tr key={header}>
                      <td><code>{header}</code></td>
                      <td>{describeTarget(target)}</td>
                    </tr>
                  ))}
                  {(r.unknown_columns ?? []).map((header) => (
                    <tr key={`unknown-${header}`}>
                      <td><code>{header}</code></td>
                      <td><Text role="caption" c="var(--rr-color-warning, inherit)">{t('contacts.import.preview.target.unknown')}</Text></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(r.unknown_columns?.length ?? 0) > 0 && (
                <Checkbox
                  label={t('contacts.import.preview.createFields', { count: r.unknown_columns.length })}
                  description={t('contacts.import.preview.createFieldsHint')}
                  checked={createFields}
                  onChange={setCreateFields}
                />
              )}
            </Stack>
          )}

          {(r.field_value_problems?.count ?? 0) > 0 && (
            <Alert color="warning">
              <Stack gap="xs">
                <Text role="bodySmall">{t('contacts.import.preview.fieldProblems', { count: r.field_value_problems.count })}</Text>
                {r.field_value_problems.examples.slice(0, 5).map((p) => (
                  <Text key={`${p.row}-${p.column}`} role="caption" c="var(--rr-color-text-muted)">
                    {t('contacts.import.preview.fieldProblem', { row: p.row, column: p.column, value: p.value })}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      )}

      <Group justify="flex-end">
        <Button size="xs" variant="default" onClick={onDone}>{t('action.cancel')}</Button>
        <Button size="xs" onClick={commit} disabled={!r || r.ok_rows === 0}>
          {t('contacts.import.preview.confirm', { count: r?.ok_rows ?? 0 })}
        </Button>
      </Group>
    </Stack>
  );
}
